import { Fragment, useState, useEffect } from "react";
import { Button, MediaQuery, Modal, Table } from "@mantine/core";
import { useSearchParams } from "react-router-dom";

import { FuelType } from "@captiva/shared-types";
import { FuelTypeAssetConfig } from "../../components/listings/configurations/assets/fuel-type-asset-config";
import { AssetEditor } from "../../components/shared/asset-editor";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { Plus } from "tabler-icons-react";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";
import { bulkGenerateAndDownloadQR } from "../../components/shared/bulk-qr-generator";
import TableRowActions from "./shared/table-row-actions";
import { BulkActionsMenu } from "./shared/bulk-actions-menu";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface FuelTypesTableProps {
  elements: FuelType[];
  auth: AuthUser | null;
  reloadCallback: () => void;
}

export function FuelTypesTable({ elements, auth, reloadCallback }: FuelTypesTableProps) {
  const newFuelType: Partial<FuelType> = {
    name: "",
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [editedElement, setEditedElement] = useState<FuelType | Partial<FuelType> | undefined>(undefined);

  // Auto-open asset editor if editId is in query params
  useEffect(() => {
    const editId = searchParams.get("editId");
    if (editId) {
      const match = elements.find((el) => el.id === editId);
      if (match) {
        setEditedElement(match);
      }
    }
  }, [searchParams, elements]);

  const closeEdit = () => {
    setEditedElement(undefined);
    if (searchParams.get("editId")) {
      searchParams.delete("editId");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();
  
  const handleCreateFlexOptionsSet = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      alert("No company ID found in auth claims.");
      return;
    }
    createFlexOptionsSet({
      assetType: "FuelTypes",
      assetList: elements,
      metadataPrefix: "fuelType",
      fieldMappings: {
        label: "name",
        value: "id",
        metadata: {
          name: "name",
        }
      },
      companyId
    });
  };

  const rows = elements.map((element) => (
    <Fragment key={element.id}>
      {/* // desktop sizes */}
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="FuelType"
              collectionName="fuelTypes"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
      {/* // phone sizes */}
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="FuelType"
              collectionName="fuelTypes"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
    </Fragment>
  ));

  async function handleBulkQRDownload() {
    await bulkGenerateAndDownloadQR({
      assetName: "FuelType",
      items: elements,
      nameKey: "name",
      idKey: "id",
      zipName: "fuel-types-qr-codes.zip",
      logoSrc: "/captiva-mark.png"
    });
  }

  return (
    <>
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ textAlign: 'left' }}>
                <BulkActionsMenu onBulkQRDownload={handleBulkQRDownload} onCreateFlexOptionsSet={handleCreateFlexOptionsSet} loading={loading} />
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      )}
      {editedElement && (
        <Modal opened onClose={closeEdit} title="Fuel Type">
          <AssetEditor
            asset={editedElement}
            assetConfig={FuelTypeAssetConfig}
            collection="fuelTypes"
            auth={auth}
            reloadCallback={() => {
              reloadCallback();
              closeEdit();
            }}
          ></AssetEditor>
        </Modal>
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={() => setEditedElement(newFuelType)}
          fullWidth
          style={{ maxWidth: 500 }}
          my="xl"
          color="cap-navy"
          variant="outline"
          leftIcon={<Plus />}
        >
          Create new
        </Button>
      </div>
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="FuelTypes"
        />
      )}
    </>
  );
}
